import React, { useEffect } from "react";
import "./index.scss";
import { imageDimensionSet, getHexColor } from "../../helpers";
import Pager from "../Pager/Pager";

type Nullable<T> = T | null;

interface PlayerPathwayTileType {
  heading: Nullable<string>;
  subheading: Nullable<string>;
  description?: Nullable<string>;
  logoUrl: Nullable<string>;
  backgroundImage: Nullable<string>;
  gradientColor: Nullable<string>;
  stats: Stat[] | null;
  grandSlams: GrandSlams | null;
}

interface Stat {
  value: number;
  label: string;
}

type GrandSlams = {
  AusOpen?: number;
  FrenchOpen?: number;
  Wimbledon?: number;
  USOpen?: number;
} | null;

export default ({
  title,
  tiles,
  sectionLink
}: {
  title?: string;
  children?: JSX.Element;
  tiles: PlayerPathwayTileType[];
  tileWidth?: number;
  sectionLink?: {
    name: string;
    url: string;
    target: "_blank" | "_self" | "_parent";
  };
  sectionLinkText?: string;
}) => {
  const styles = `${tiles.reduce((str, t, k) => {
    let styles =
      str +
      `
        .player-pathway-tile__wrapper--${k} {
            z-index: ${tiles.length - k};
        }
        .player-pathway-tile--${k}:before {
            background-image: linear-gradient(to bottom, ${getHexColor(
              t.gradientColor ? t.gradientColor : "000000"
            )} 5%, transparent);
        }`;
    return styles;
  }, "")}`;

  useEffect(() => {
    const head = document.querySelector("head");
    if (head !== null) {
      const styleNode = document.createElement("style");
      styleNode.id = "stats";
      styleNode.innerHTML = styles;
      if (document) head.appendChild(styleNode);
    }
  }, []);

  return (
    <section className="page-section">
      <div className="page-section__inner">
        <div className="player-pathway-tiles__wrapper">
          {title && <h2>{title}</h2>}
          <Pager
            sectionLink={sectionLink}
            fadedEdge
            tileClassName={"player-pathway-tile"}
          >
            <>
              {tiles.map((tile: PlayerPathwayTileType, key: number) => (
                <div
                  key={key}
                  className={`player-pathway-tile__wrapper player-pathway-tile__wrapper--${key}`}
                >
                  <div
                    style={{
                      backgroundImage: `url("${
                        tile.backgroundImage
                          ? imageDimensionSet({
                              url: tile.backgroundImage,
                              width: 500,
                              height: 390
                            })
                          : "unset"
                      }")`
                    }}
                    key={key}
                    className={`player-pathway-tile player-pathway-tile--${key}`}
                  >
                    {tile.subheading && (
                      <span className="player-pathway-tile__subheading">
                        {tile.subheading}
                      </span>
                    )}
                    {tile.heading && (
                      <span
                        style={{
                          textAlign: !!!tile.subheading ? "center" : "unset"
                        }}
                        className="player-pathway-tile__heading"
                      >
                        {tile.heading}
                      </span>
                    )}
                    {tile.logoUrl && !tile.grandSlams && (
                      <div
                        style={{
                          backgroundImage: `url("${tile.logoUrl}")`
                        }}
                        className="player-pathway-tile__logo"
                      >
                        LOGO
                      </div>
                    )}
                    {tile.stats && !tile.grandSlams && (
                      <ul
                        className={`player-pathway-tile__stats${
                          tile.stats && tile.stats.length === 1 ? " single" : ""
                        }`}
                      >
                        {tile.stats.map((stat: Stat, key: number) => (
                          <li className="player-pathway-tile__stat" key={key}>
                            <span className="player-pathway-tile__stat-value">
                              {stat.value}
                            </span>
                            <span className="player-pathway-tile__stat-label">
                              {stat.label}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {tile.grandSlams &&
                      Object.keys(tile.grandSlams).length !== 0 && (
                        <ul className="player-pathway-tile__grand-slams">
                          {Object.keys(tile.grandSlams).map(
                            (label: keyof GrandSlams) => (
                              <li
                                className={`player-pathway-tile__grand-slam ${label}`}
                                key={label}
                              >
                                <span className="player-pathway-tile__grand-slam-value">
                                  {tile.grandSlams && tile.grandSlams[label]}
                                </span>
                              </li>
                            )
                          )}
                        </ul>
                      )}
                    {tile.stats &&
                      tile.stats.length == 0 &&
                      !tile.grandSlams &&
                      tile.description &&
                      tile.description.length && (
                        <p className="player-pathway-tile__description">
                          {tile.description}
                        </p>
                      )}
                  </div>
                  <span className="icon"></span>
                </div>
              ))}
            </>
          </Pager>
        </div>
      </div>
    </section>
  );
};
